import React, { useEffect, useState } from 'react';

import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    ListItemText,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';

import { IconEdit, IconPlus, IconTrash, IconX, IconArrowsSplit, IconGripVertical } from '@tabler/icons';

import api from '../../../../services/api';

const TYPE_LABELS = {
    text: 'Texto',
    number: 'Número',
    coin: 'Moeda',
    phone: 'Telefone',
    email: 'E-mail',
    date: 'Data',
    time: 'Hora',
    select: 'Lista suspensa',
    boolean: 'Verdadeiro/Falso'
};

// Dois tipos exclusivos da aba Proposta (`propostaExtras`) - não fazem
// sentido nas outras entidades (Cliente/Negócio/...), por isso não entram
// em `TYPE_LABELS` (a lista oferecida na criação/edição do campo), mas
// entram aqui pra sempre existir um rótulo pra mostrar na tabela, mesmo
// que um campo desse tipo já exista (nunca desaparece o nome do tipo só
// porque o dropdown de criação não o oferece).
const PROPOSTA_TYPE_LABELS = {
    lista_itens: 'Lista de Itens',
    signature_space: 'Incluir espaço para assinatura'
};

const ALL_TYPE_LABELS = { ...TYPE_LABELS, ...PROPOSTA_TYPE_LABELS };

// Tipos aceitos numa COLUNA de 'Lista de Itens' - só os que fazem sentido
// dentro de uma célula de tabela (nada de lista suspensa/booleano/outra
// lista dentro da lista).
const LISTA_ITENS_COLUNA_TYPE_LABELS = { text: 'Texto', number: 'Número', coin: 'Moeda', date: 'Data' };

const EMPTY_FORM = { name: '', type_field: 'text', options: [], required: false, hide_label: false };

const EMPTY_COLUNA_DRAFT = { title: '', type_field: 'text' };

const slugify = (texto) =>
    (texto || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const EMPTY_CONDITION_FORM = { trigger_field_id: '', trigger_value: '', target_field_ids: [] };

// Painel genérico de CRUD de definições de campo customizado - usado dentro
// das abas do hub "Campos Customizados" (Clientes/Empresas/Negócios/
// Produtos/Serviços/Eventos). `apiBasePath` é o trecho da URL logo após
// `v1/apps/{appId}/` (ex: "organization_fields", "client_fields", ou
// "pipelines/{id}/deal_fields" pra Negócios) - null enquanto o pré-requisito
// daquela aba (ex: escolher um pipeline) ainda não foi satisfeito.
// `extraParams` (opcional, ex: `{tipo: 'produto'}`) vai como query string
// nas chamadas de listar/criar - usado pra escopar `item_fields` (Produtos
// vs Serviços compartilham o mesmo `apiBasePath`, diferenciados só por
// `tipo`, ao contrário de Negócios que tem uma rota aninhada própria por
// pipeline). O painel continua sem nenhuma lógica específica de entidade.
const buildQueryString = (params) => {
    if (!params || Object.keys(params).length === 0) return '';
    return `?${new URLSearchParams(params).toString()}`;
};

const FieldsSettingsPanel = ({
    appId,
    apiBasePath,
    entityLabel,
    entityLabelPlural,
    entityDescription,
    extraParams,
    // Só usados hoje pela aba "Proposta" - reordenar por drag-and-drop e
    // marcar um campo pra não mostrar o nome dele no documento (frases
    // prontas tipo "Obrigado, Fulano"). Ficam opt-in porque não fazem
    // sentido pras outras abas (Clientes, Negócios etc não têm PDF nenhum
    // renderizando esses campos pro cliente ver).
    sortable = false,
    supportsHideLabel = false,
    // Só a aba Proposta - libera dois tipos exclusivos no dropdown de
    // criação/edição ('Lista de Itens' e 'Incluir espaço para
    // assinatura', ver `PROPOSTA_TYPE_LABELS`).
    propostaExtras = false
}) => {
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [reordering, setReordering] = useState(false);
    const dragFieldId = React.useRef(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [newOptionText, setNewOptionText] = useState('');
    // Rascunho de uma coluna nova da 'Lista de Itens' - `formData.options`
    // guarda a lista já confirmada (`[{name,title,type_field}, ...]`).
    const [novaColuna, setNovaColuna] = useState(EMPTY_COLUNA_DRAFT);

    const typeLabels = propostaExtras ? { ...TYPE_LABELS, ...PROPOSTA_TYPE_LABELS } : TYPE_LABELS;

    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
    const [conditionForm, setConditionForm] = useState(EMPTY_CONDITION_FORM);
    const [savingCondition, setSavingCondition] = useState(false);

    const loadFields = async () => {
        setLoading(true);
        try {
            const r = await api.get(`v1/apps/${appId}/${apiBasePath}/${buildQueryString(extraParams)}`);
            setFields(r.data || []);
        } catch {
            setStatus({ type: 'error', message: 'Erro ao carregar campos.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!appId || !apiBasePath) return;
        loadFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, apiBasePath, JSON.stringify(extraParams)]);

    const setField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

    const openCreate = () => {
        setEditingId(null);
        setFormData(EMPTY_FORM);
        setNewOptionText('');
        setNovaColuna(EMPTY_COLUNA_DRAFT);
        setDialogOpen(true);
    };

    const openEdit = (f) => {
        setEditingId(f.id);
        setFormData({
            name: f.title,
            type_field: f.type_field,
            options: f.options || [],
            required: f.required,
            hide_label: f.hide_label || false
        });
        setNewOptionText('');
        setNovaColuna(EMPTY_COLUNA_DRAFT);
        setDialogOpen(true);
    };

    const addOption = () => {
        const valor = newOptionText.trim();
        if (!valor || formData.options.includes(valor)) return;
        setField('options', [...formData.options, valor]);
        setNewOptionText('');
    };

    const removeOption = (valor) => {
        setField(
            'options',
            formData.options.filter((o) => o !== valor)
        );
    };

    // Colunas da 'Lista de Itens' - `formData.options` guarda
    // `[{name, title, type_field}, ...]` (mesmo JSONField `options` que um
    // campo 'select' usa pra guardar `[string, ...]` - o formato muda
    // conforme o tipo do campo, não é uma coluna nova no banco).
    const addColuna = () => {
        const titulo = novaColuna.title.trim();
        if (!titulo) return;
        const name = slugify(titulo);
        if (!name || formData.options.some((c) => c.name === name)) return;
        setField('options', [...formData.options, { name, title: titulo, type_field: novaColuna.type_field }]);
        setNovaColuna(EMPTY_COLUNA_DRAFT);
    };

    const removeColuna = (name) => {
        setField(
            'options',
            formData.options.filter((c) => c.name !== name)
        );
    };

    const handleSave = async () => {
        if (!formData.name) {
            setStatus({ type: 'error', message: 'Informe o nome do campo.' });
            return;
        }
        if (formData.type_field === 'select' && formData.options.length === 0) {
            setStatus({ type: 'error', message: 'Adicione pelo menos um valor pra lista suspensa.' });
            return;
        }
        if (formData.type_field === 'lista_itens' && formData.options.length === 0) {
            setStatus({ type: 'error', message: 'Adicione pelo menos uma coluna pra Lista de Itens.' });
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await api.patch(`v1/apps/${appId}/${apiBasePath}/${editingId}/`, {
                    title: formData.name,
                    type_field: formData.type_field,
                    options: formData.options,
                    required: formData.required,
                    ...(supportsHideLabel ? { hide_label: formData.hide_label } : {})
                });
                setStatus({ type: 'success', message: 'Campo atualizado!' });
            } else {
                await api.post(`v1/apps/${appId}/${apiBasePath}/${buildQueryString(extraParams)}`, { field: formData, ...formData });
                setStatus({ type: 'success', message: 'Campo criado!' });
            }
            setDialogOpen(false);
            loadFields();
        } catch (e) {
            setStatus({ type: 'error', message: e.response?.data?.error || 'Erro ao salvar campo.' });
        } finally {
            setSaving(false);
        }
    };

    // Drag-and-drop nativo (sem lib) nas linhas da tabela - só ativo quando
    // `sortable` é true. Atualiza a ordem na tela na hora (otimista) e
    // manda a lista inteira pro backend persistir.
    const handleRowDragStart = (fieldId) => {
        dragFieldId.current = fieldId;
    };

    const handleRowDragOver = (e) => {
        e.preventDefault();
    };

    const handleRowDrop = async (targetFieldId) => {
        const draggedId = dragFieldId.current;
        dragFieldId.current = null;
        if (!draggedId || draggedId === targetFieldId) return;

        const semArrastado = fields.filter((f) => f.id !== draggedId);
        const targetIndex = semArrastado.findIndex((f) => f.id === targetFieldId);
        const arrastado = fields.find((f) => f.id === draggedId);
        const novaOrdem = [...semArrastado.slice(0, targetIndex), arrastado, ...semArrastado.slice(targetIndex)];

        setFields(novaOrdem);
        setReordering(true);
        try {
            await api.post(`v1/apps/${appId}/${apiBasePath}/reorder/`, { order: novaOrdem.map((f) => f.id) });
        } catch (e) {
            setStatus({ type: 'error', message: 'Erro ao salvar a nova ordem.' });
            loadFields();
        } finally {
            setReordering(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await api.delete(`v1/apps/${appId}/${apiBasePath}/${deleteId}/`);
            setFields((prev) => prev.filter((f) => f.id !== deleteId));
            setDeleteId(null);
            setStatus({ type: 'success', message: 'Campo removido.' });
        } catch {
            setStatus({ type: 'error', message: 'Erro ao excluir campo.' });
        } finally {
            setDeleting(false);
        }
    };

    // Campos elegíveis pra disparar uma condição - exclui os que já são
    // condicionais (só aparecem eles mesmos condicionalmente), senão dava
    // pra criar um ciclo (campo A depende de B, e B depende de A) onde os
    // dois ficam escondidos um esperando o outro.
    const triggerCandidates = fields.filter((f) => !f.condition_field);
    const triggerField = fields.find((f) => f.id === conditionForm.trigger_field_id);
    // Campos elegíveis como alvo - ainda sem condição nenhuma (cada campo só
    // pode ter uma condição) e diferente do próprio gatilho escolhido.
    const targetCandidates = fields.filter((f) => !f.condition_field && f.id !== conditionForm.trigger_field_id);

    const openConditionDialog = () => {
        setConditionForm(EMPTY_CONDITION_FORM);
        setConditionDialogOpen(true);
    };

    const handleChangeTriggerField = (fieldId) => {
        // Muda o gatilho reseta valor e alvos - as opções de valor (se o
        // gatilho for lista suspensa) e a lista de alvos elegíveis dependem
        // de qual campo foi escolhido, então a seleção anterior pode não
        // fazer mais sentido.
        setConditionForm({ trigger_field_id: fieldId, trigger_value: '', target_field_ids: [] });
    };

    const handleSaveCondition = async () => {
        if (!conditionForm.trigger_field_id || !conditionForm.trigger_value || conditionForm.target_field_ids.length === 0) {
            setStatus({ type: 'error', message: 'Preencha o campo, o valor e selecione ao menos um campo pra mostrar.' });
            return;
        }
        setSavingCondition(true);
        try {
            await Promise.all(
                conditionForm.target_field_ids.map((targetId) =>
                    api.patch(`v1/apps/${appId}/${apiBasePath}/${targetId}/`, {
                        condition_field: conditionForm.trigger_field_id,
                        condition_value: conditionForm.trigger_value
                    })
                )
            );
            setStatus({ type: 'success', message: 'Condição adicionada!' });
            setConditionDialogOpen(false);
            loadFields();
        } catch (e) {
            setStatus({ type: 'error', message: e.response?.data?.error || 'Erro ao salvar condição.' });
        } finally {
            setSavingCondition(false);
        }
    };

    const handleRemoveCondition = async (fieldId) => {
        try {
            await api.patch(`v1/apps/${appId}/${apiBasePath}/${fieldId}/`, { condition_field: null, condition_value: null });
            setStatus({ type: 'success', message: 'Condição removida.' });
            loadFields();
        } catch {
            setStatus({ type: 'error', message: 'Erro ao remover condição.' });
        }
    };

    if (!apiBasePath) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ opacity: 0.5, fontWeight: 600 }}>Selecione um pipeline acima pra ver/criar os campos.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640, lineHeight: 1.7 }}>
                    {entityDescription}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                        variant="outlined"
                        startIcon={<IconArrowsSplit size={18} />}
                        onClick={openConditionDialog}
                        disabled={fields.length < 2}
                        sx={{ borderRadius: '14px', px: 2.5, height: 44, textTransform: 'none', fontWeight: 700 }}
                    >
                        Adicionar Condições
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={18} />}
                     onClick={openCreate}
                        sx={{
                            borderRadius: '14px',
                            px: 2.5,
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                        }}
                    >
                        Novo campo
                    </Button>
                </Stack>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ my: 2, borderRadius: '16px' }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ borderRadius: '20px', overflow: 'hidden', mt: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : fields.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, opacity: 0.4 }}>
                            Nenhum campo customizado criado ainda.
                        </Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {sortable && <TableCell sx={{ width: 36 }} />}
                                <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Obrigatório</TableCell>
                                {supportsHideLabel && <TableCell sx={{ fontWeight: 700 }}>No documento</TableCell>}
                                <TableCell sx={{ fontWeight: 700 }}>Condição</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fields.map((f) => (
                                <TableRow
                                    key={f.id}
                                    hover
                                    draggable={sortable}
                                    onDragStart={sortable ? () => handleRowDragStart(f.id) : undefined}
                                    onDragOver={sortable ? handleRowDragOver : undefined}
                                    onDrop={sortable ? () => handleRowDrop(f.id) : undefined}
                                    sx={sortable ? { cursor: 'grab', opacity: reordering ? 0.6 : 1 } : undefined}
                                >
                                    {sortable && (
                                        <TableCell sx={{ color: 'text.disabled' }}>
                                            <IconGripVertical size={16} />
                                        </TableCell>
                                    )}
                                    <TableCell>{f.title}</TableCell>
                                    <TableCell>{ALL_TYPE_LABELS[f.type_field] || f.type_field}</TableCell>
                                    <TableCell>
                                        {f.required ? (
                                            <Chip label="Obrigatório" size="small" color="warning" sx={{ fontWeight: 700 }} />
                                        ) : (
                                            <Chip label="Opcional" size="small" sx={{ fontWeight: 700 }} />
                                        )}
                                    </TableCell>
                                    {supportsHideLabel && (
                                        <TableCell>
                                            {f.hide_label ? (
                                                <Chip label="Só o valor" size="small" color="info" sx={{ fontWeight: 700 }} />
                                            ) : (
                                                <Chip label="Nome + valor" size="small" sx={{ fontWeight: 700 }} />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {f.condition_field ? (
                                            <Chip
                                                label={`Só aparece se "${fields.find((x) => x.id === f.condition_field)?.title || '?'}" = "${f.condition_value}"`}
                                                size="small"
                                                onDelete={() => handleRemoveCondition(f.id)}
                                                sx={{ fontWeight: 600, maxWidth: 320 }}
                                            />
                                        ) : (
                                            <Typography variant="body2" sx={{ opacity: 0.4 }}>
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => openEdit(f)} sx={{ mr: 1 }}>
                                                <IconEdit size={16} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton size="small" onClick={() => setDeleteId(f.id)} sx={{ color: '#ef4444' }}>
                                                <IconTrash size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Card>

            {sortable && fields.length > 1 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    Arraste as linhas pela lateral pra reordenar - é a ordem que os campos aparecem no PDF final.
                </Typography>
            )}

            {/* CREATE / EDIT DIALOG */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                    <Typography variant="h4" fontWeight={800}>
                        {editingId ? 'Editar campo' : `Novo campo de ${entityLabel}`}
                    </Typography>
                    <IconButton onClick={() => setDialogOpen(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 2 }}>
                        <TextField
                            label="Nome do campo"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setField('name', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        />

                        <TextField
                            label="Tipo"
                            select
                            fullWidth
                            value={formData.type_field}
                            onChange={(e) => setField('type_field', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        >
                            {Object.entries(typeLabels).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>

                        {formData.type_field === 'lista_itens' && (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Colunas da tabela
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        label="Nome da coluna"
                                        fullWidth
                                        value={novaColuna.title}
                                        onChange={(e) => setNovaColuna({ ...novaColuna, title: e.target.value })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addColuna();
                                            }
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                                    />
                                    <TextField
                                        label="Tipo"
                                        select
                                        value={novaColuna.type_field}
                                        onChange={(e) => setNovaColuna({ ...novaColuna, type_field: e.target.value })}
                                        sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                                    >
                                        {Object.entries(LISTA_ITENS_COLUNA_TYPE_LABELS).map(([value, label]) => (
                                            <MenuItem key={value} value={value}>
                                                {label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Button
                                        variant="outlined"
                                        onClick={addColuna}
                                        sx={{ borderRadius: '12px', textTransform: 'none', flexShrink: 0 }}
                                    >
                                        Adicionar
                                    </Button>
                                </Stack>

                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: formData.options.length > 0 ? 1.5 : 0 }}>
                                    {formData.options.map((coluna) => (
                                        <Chip
                                            key={coluna.name}
                                            label={`${coluna.title} (${LISTA_ITENS_COLUNA_TYPE_LABELS[coluna.type_field] || coluna.type_field})`}
                                            onDelete={() => removeColuna(coluna.name)}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    ))}
                                </Stack>

                                {formData.options.length === 0 && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        Nenhuma coluna adicionada ainda - na hora de montar a proposta, um botão "+" deixa adicionar quantas
                                        linhas quiser nessas colunas.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {formData.type_field === 'signature_space' && (
                            <Alert severity="info" sx={{ borderRadius: '14px' }}>
                                Quando marcado numa Proposta específica, reserva um espaço de assinatura no canto inferior direito do PDF
                                gerado.
                            </Alert>
                        )}

                        {formData.type_field === 'select' && (
                            <Box>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        label="Adicionar valor"
                                        fullWidth
                                        value={newOptionText}
                                        onChange={(e) => setNewOptionText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addOption();
                                            }
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={addOption}
                                        sx={{ borderRadius: '12px', textTransform: 'none', flexShrink: 0 }}
                                    >
                                        Adicionar
                                    </Button>
                                </Stack>

                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: formData.options.length > 0 ? 1.5 : 0 }}>
                                    {formData.options.map((opt) => (
                                        <Chip key={opt} label={opt} onDelete={() => removeOption(opt)} sx={{ fontWeight: 600 }} />
                                    ))}
                                </Stack>

                                {formData.options.length === 0 && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        Nenhum valor adicionado ainda - quem preencher esse campo só vai poder escolher entre os valores
                                        cadastrados aqui.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <FormControlLabel
                            control={<Switch checked={formData.required} onChange={(e) => setField('required', e.target.checked)} />}
                            label="Obrigatório"
                        />

                        {supportsHideLabel && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch checked={formData.hide_label} onChange={(e) => setField('hide_label', e.target.checked)} />
                                    }
                                    label="Mostrar só o valor no documento (esconder o nome do campo)"
                                />
                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                    Use pra frases prontas - ex: um campo "Frase de agradecimento" com o valor "Obrigado, Fulano"
                                    aparece só como "Obrigado, Fulano" no PDF, sem o nome do campo na frente.
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setDialogOpen(false)} sx={{ borderRadius: '12px', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        sx={{ borderRadius: '12px', textTransform: 'none', px: 3, background: 'linear-gradient(135deg,#2563eb,#7c3aed)' }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : editingId ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DELETE CONFIRM */}
            <Dialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px' } }}
            >
                <DialogTitle>Excluir campo</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza? Os valores já preenchidos para esse campo em {entityLabelPlural} existentes serão perdidos.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="outlined" onClick={() => setDeleteId(null)} sx={{ borderRadius: '12px', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ borderRadius: '12px', textTransform: 'none' }}
                    >
                        {deleting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CONDIÇÕES DE EXIBIÇÃO */}
            <Dialog
                open={conditionDialogOpen}
                onClose={() => setConditionDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                    <Typography variant="h4" fontWeight={800}>
                        Adicionar Condições
                    </Typography>
                    <IconButton onClick={() => setConditionDialogOpen(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 2 }}>
                        <TextField
                            label="Ao preencher este campo"
                            select
                            fullWidth
                            value={conditionForm.trigger_field_id}
                            onChange={(e) => handleChangeTriggerField(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        >
                            {triggerCandidates.map((f) => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.title}
                                </MenuItem>
                            ))}
                        </TextField>

                        {triggerField && triggerField.type_field === 'select' ? (
                            <TextField
                                label="Se o valor for"
                                select
                                fullWidth
                                value={conditionForm.trigger_value}
                                onChange={(e) => setConditionForm({ ...conditionForm, trigger_value: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                            >
                                {(triggerField.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <TextField
                                label="Se o valor for"
                                fullWidth
                                disabled={!triggerField}
                                value={conditionForm.trigger_value}
                                onChange={(e) => setConditionForm({ ...conditionForm, trigger_value: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                            />
                        )}

                        <TextField
                            label="Mostra esses campos"
                            select
                            fullWidth
                            disabled={!triggerField}
                            value={conditionForm.target_field_ids}
                            onChange={(e) =>
                                setConditionForm({
                                    ...conditionForm,
                                    target_field_ids: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                                })
                            }
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) =>
                                    selected.map((id) => fields.find((f) => f.id === id)?.title || '?').join(', ')
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        >
                            {targetCandidates.map((f) => (
                                <MenuItem key={f.id} value={f.id}>
                                    <Checkbox checked={conditionForm.target_field_ids.includes(f.id)} size="small" />
                                    <ListItemText primary={f.title} />
                                </MenuItem>
                            ))}
                        </TextField>

                        {triggerField && targetCandidates.length === 0 && (
                            <Alert severity="info" sx={{ borderRadius: '14px' }}>
                                Não há campos disponíveis pra receber essa condição - todos os outros campos já têm uma condição, ou só
                                existe esse campo.
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setConditionDialogOpen(false)}
                        sx={{ borderRadius: '12px', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveCondition}
                        disabled={savingCondition}
                        sx={{ borderRadius: '12px', textTransform: 'none', px: 3, background: 'linear-gradient(135deg,#2563eb,#7c3aed)' }}
                    >
                        {savingCondition ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FieldsSettingsPanel;
